import React, { useState, useEffect }  from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import TeamBadge from './TeamBadge';
import matchService from '../services/matchService';
import { useTranslation } from 'react-i18next';

const LandingPage = () => {
  const [matches, setMatches] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('matches');
  const [openFaq, setOpenFaq] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchMatches = async () => {
      const data = await matchService.getLandingPageMatches();
      setMatches(data);
    };
    fetchMatches();
  }, []);

  // Plovoucí menu: po odscrollování přidáme výraznější pozadí
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Plynulé odscrollování na kotvu z plovoucího menu
  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}.${date.getMonth()+1}. ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const customStyles = `
    .app-card-shadow {
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    }
    .app-card-hover:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    }
    /* Jemný přechod pro texty */
    .text-gradient {
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
      background-image: linear-gradient(to right, #4DA8DA, #28A745);
    }

    /* Animované gradientové pozadí showcase sekce */
    .animated-gradient {
      background: linear-gradient(-45deg, #4DA8DA, #28A745, #3b8bc0, #1e9e4a);
      background-size: 400% 400%;
      animation: scoreoGradient 15s ease infinite;
    }
    @keyframes scoreoGradient {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @media (prefers-reduced-motion: reduce) {
      .animated-gradient { animation: none; }
    }
    /* Skrytí scrollbaru pro Chrome, Safari and Opera */
    .no-scrollbar::-webkit-scrollbar {
      display: none;
    }
    /* Skrytí scrollbaru pro IE, Edge and Firefox */
    .no-scrollbar {
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }
  `;

  // --- Přepínací nabídka (segmented control) ---
  const tabs = [
    { id: 'matches',     label: 'Zápasy' },
    { id: 'leaderboard', label: 'Žebříček' },
    { id: 'ai',          label: 'AI Skaut' },
  ];

  const faqs = [
    {
      q: 'Hraje se o peníze?',
      a: 'Ne. Scoreo není sázkovka. Nevkládáš ani nevyhráváš peníze – jde čistě o fanouškovský souboj, protože každý máme toho jednoho kamaráda, kterého musíme porazit!',
    },
    {
      q: 'Jaké sporty a soutěže můžu tipovat?',
      a: 'Fotbal i hokej. Aktuálně česká nejvyšší soutěž, Liga mistrů, Evropská liga, Konferenční liga, mistrovství světa. Postupně přidáváme další soutěže.',
    },
    {
      q: 'Jak funguje bodování?',
      a: 'Čím přesnější tip na koncové skóre zápasu, tím více bodů dostaneš. ',
    },
    {
      q: 'Kdy uvidím výsledky svých tipů?',
      a: 'Prakticky okamžitě. Nejsme Livesport, takže s nějakou prodlevou počítej, ale během minuty uvidíš aktualizované nejen skóre, ale rovnou i hodnocení svých tipů a postavení v tabulce',
    },
    {
      q: 'Můžu se přidat v půlce sezóny?',
      a: 'Scoreo počítá jak celkový tak měsíční žebříček. Nestihnul si začátek sezóny? Nevadí, nic ti nebrání v tom ovládnout měsíční žebříčky!',
    },
    {
      q: 'Existují i veřejné soutěže?',
      a: 'Pokud nemáš dost kamarádů na soukromou ligu, nevadí! Přidej se do veřejných lig a změř své síly se zbytkem komunity. Celkový žebříček je globální, takže nezáleží jestli soutěžíš v rámci soukromé nebo veřejné ligy.',
    },
    {
      q: 'Jak funguje AI Skaut?',
      a: 'AI Skaut projede formu týmů, historii a data a navrhne ti tip i odhad skóre. Máš na něj 1 kredit denně – jestli ho použiješ, je na tobě.',
    },
    {
      q: 'Jak založím ligu pro svou partu?',
      a: 'Po registraci si během chvilky vytvoříš vlastní soukromou ligu a pošleš kamarádům pozvánku.',
    }
  ];

  const leaderboard = [
    { rank: 1, name: 'Honza_Tipér',   total: 284, day: 12, dayWinner: true },
    { rank: 2, name: 'Pavla K.',      total: 271, day: 6 },
    { rank: 3, name: 'MartinSpartan', total: 265, day: 9 },
    { rank: 4, name: 'Ty',            total: 248, day: 7, you: true },
    { rank: 5, name: 'LiborH',        total: 233, day: 4 },
  ];

  // --- Animace odkrývání při scrollu ---
  const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.5, ease: 'easeOut' },
  };
  const staggerWrap = {
    initial: 'hidden',
    whileInView: 'show',
    viewport: { once: true, margin: '-80px' },
    variants: { hidden: {}, show: { transition: { staggerChildren: 0.12 } } },
  };
  // Posun + fade pro položky (tam, kde nevadí inline transform)
  const fadeItem = {
    variants: {
      hidden: { opacity: 0, y: 20 },
      show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
    },
  };
  // Pouze fade – pro karty s CSS hover efektem (zachová hover posun)
  const fadeItemOpacity = {
    variants: {
      hidden: { opacity: 0 },
      show: { opacity: 1, transition: { duration: 0.5, ease: 'easeOut' } },
    },
  };

  const renderWindowChrome = (children) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl shadow-black/10 overflow-hidden">
      {/* Lišta okna */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
        <span className="w-3 h-3 rounded-full bg-red-400"></span>
        <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
        <span className="w-3 h-3 rounded-full bg-green-400"></span>
        <div className="ml-3 flex-1 max-w-xs h-5 rounded-md bg-white border border-gray-200 text-[10px] text-gray-400 flex items-center px-2">
          scoreo.cz/app
        </div>
      </div>
      <div className="p-4 sm:p-8 min-h-[420px]">{children}</div>
    </div>
  );

  const renderTabPanel = () => {
    switch (activeTab) {
      case 'leaderboard':
        return (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="font-bold text-lg text-[#002855]">Liga: Kancelář Open</h4>
                <span className="text-sm text-gray-400">Sezóna 2025/26 · 12 hráčů</span>
              </div>
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-green-100 text-green-700">Live</span>
            </div>

            <div className="rounded-lg overflow-hidden bg-white shadow-[0_4px_12px_rgba(0,0,0,0.06)]">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#F9F9F9] text-[#787878] uppercase" style={{ letterSpacing: '0.5px' }}>
                    <th className="font-light text-center py-3 px-3 w-12 sm:w-16">#</th>
                    <th className="font-light text-left py-3 px-3">Hráč</th>
                    <th className="font-light text-center py-3 px-3 w-16 sm:w-20">Body</th>
                    <th className="font-light text-right py-3 px-3 w-16 sm:w-24">Celkem</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((p) => {
                    const top3 = p.rank <= 3;
                    const rowBg = p.rank === 1 ? 'rgba(255, 215, 0, 0.075)'
                      : p.rank === 2 ? 'rgba(192, 192, 192, 0.075)'
                      : p.rank === 3 ? 'rgba(205, 127, 50, 0.075)'
                      : '#FFFFFF';
                    const textColor = p.rank === 1 ? '#998100'
                      : p.rank === 2 ? '#9A9A9A'
                      : p.rank === 3 ? '#CD7F32'
                      : '#374151';
                    const medal = p.rank === 1 ? '🏆' : p.rank === 2 ? '🥈' : p.rank === 3 ? '🥉' : p.rank;
                    return (
                      <tr
                        key={p.rank}
                        style={{ backgroundColor: rowBg }}
                        className="border-b border-[#e0e0e0] last:border-0"
                      >
                        <td
                          className={`text-center py-3 px-3 ${top3 ? 'font-bold' : ''}`}
                          style={{ color: textColor }}
                        >
                          {medal}
                        </td>
                        <td className="text-left py-3 px-3" style={{ color: p.you ? '#4DA8DA' : textColor }}>
                          <span className={top3 || p.you ? 'font-bold' : ''}>{p.name}</span>
                          {p.you && <span className="text-xs text-[#4DA8DA] font-normal ml-1">(ty)</span>}
                          {p.dayWinner && (
                            <span className="ml-1.5 align-middle" title="Nejvíc bodů tento den">🔥</span>
                          )}
                        </td>
                        <td className="text-center py-3 px-3 font-bold" style={{ color: '#4CAF50' }}>
                          {p.day}
                        </td>
                        <td
                          className={`text-right py-3 px-3 ${top3 ? 'font-bold' : ''}`}
                          style={{ color: textColor }}
                        >
                          {p.total}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center">Klikni na hráče a uvidíš všechny jeho tipy</p>
          </div>
        );

      case 'matches':
        return (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h4 className="font-bold text-lg text-[#002855]">Nadcházející zápasy</h4>
              <span className="text-sm text-gray-400">Tipuj do výkopu</span>
            </div>
            {matches.length === 0 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {[0,1,2,3].map((i) => (
                  <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse"></div>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {matches.slice(0, 4).map((match) => {
                  const isLive = match.matchStatus !== 'NS' && match.matchStatus !== 'FT';
                  return (
                    <div key={match.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                        <span className="font-bold">{formatTime(match.date)}</span>
                        <span className={`px-2 py-0.5 rounded font-semibold ${isLive ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                          {match.matchStatus === 'NS' ? 'Tipovat' : (isLive ? '• Live' : 'Konec')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col items-center w-1/3">
                          <TeamBadge teamId={match.homeTeam.id} teamName={match.homeTeam.name} />
                          <span className="text-xs font-bold mt-1 text-center truncate w-full">
                            {t(`teams.${match.homeTeam.name}`, { defaultValue: match.homeTeam.name })}
                          </span>
                        </div>
                        <div className="text-lg font-bold text-[#002855]">
                          {match.matchStatus === 'NS' ? 'VS' : `${match.scoreHome}:${match.scoreAway}`}
                        </div>
                        <div className="flex flex-col items-center w-1/3">
                          <TeamBadge teamId={match.awayTeam.id} teamName={match.awayTeam.name} />
                          <span className="text-xs font-bold mt-1 text-center truncate w-full">
                            {t(`teams.${match.awayTeam.name}`, { defaultValue: match.awayTeam.name })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'ai':
        return (
          <div>
            <div className="bg-gradient-to-r from-[#002855] to-[#0f3460] rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-72 h-72 bg-[#7C3AED] opacity-20 blur-[90px] rounded-full translate-x-1/2 -translate-y-1/2"></div>
              <div className="relative z-10">
                <div className="inline-block bg-[#7C3AED] px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider mb-4 border border-white/20">
                  AI Powered
                </div>
                <div className="flex justify-between items-center mb-6 pb-6 border-b border-white/10">
                  <div>
                    <span className="block text-xs text-gray-400 uppercase tracking-wide">Předpověď</span>
                    <span className="text-lg font-bold">Sparta vs. Slavia</span>
                  </div>
                  <div className="text-right">
                    <span className="block text-xs text-gray-400 uppercase tracking-wide">Důvěra</span>
                    <span className="text-[#a78bfa] font-bold text-lg">78%</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg">
                    <span className="text-gray-300 text-sm">Pravděpodobný vítěz</span>
                    <span className="font-bold">Remíza</span>
                  </div>
                  <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg">
                    <span className="text-gray-300 text-sm">Odhadované skóre</span>
                    <span className="font-bold text-xl text-[#a78bfa]">1 : 1</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#002855] font-montserrat overflow-x-hidden">
     <SEO
        title="Domů"
        description="Tipuj výsledky zápasů, založ ligu s přáteli a ukaž, kdo je největší expert. Scoreo je fotbalová a hokejová tipovačka zdarma."
      />
      <style>{customStyles}</style>

      {/* --- PLOVOUCÍ MENU (sticky pill) --- */}
      <nav className="fixed top-3 sm:top-4 inset-x-0 z-50 px-3 sm:px-4">
        <div
          className={`max-w-5xl mx-auto flex items-center justify-between gap-4 rounded-full px-3 sm:px-5 py-2.5 transition-all duration-300 border ${
            scrolled
              ? 'bg-white/80 backdrop-blur-md border-gray-200 shadow-lg shadow-black/5'
              : 'bg-white/60 backdrop-blur border-white/50 shadow-sm'
          }`}
        >
          {/* Logo */}
          <Link
            to="/"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="text-lg sm:text-xl font-bold tracking-tight text-[#002855] pl-1"
          >
            Scoreo
          </Link>

          {/* Středové odkazy */}
          <div className="hidden md:flex items-center gap-1 text-sm font-semibold text-gray-500">
            <a href="#showcase" onClick={(e) => handleNavClick(e, 'showcase')} className="px-3 py-2 rounded-full hover:bg-gray-100 hover:text-[#002855] transition-colors">Náhled</a>
            <a href="#features" onClick={(e) => handleNavClick(e, 'features')} className="px-3 py-2 rounded-full hover:bg-gray-100 hover:text-[#002855] transition-colors">Funkce</a>
            <a href="#ai" onClick={(e) => handleNavClick(e, 'ai')} className="px-3 py-2 rounded-full hover:bg-gray-100 hover:text-[#002855] transition-colors">AI Skaut</a>
            <a href="#faq" onClick={(e) => handleNavClick(e, 'faq')} className="px-3 py-2 rounded-full hover:bg-gray-100 hover:text-[#002855] transition-colors">FAQ</a>
          </div>

          {/* Akce */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              className="text-xs sm:text-sm font-semibold text-gray-600 hover:text-[#4DA8DA] transition-colors whitespace-nowrap px-2"
            >
              Přihlásit
            </Link>
            <Link
              to="/register"
              className="bg-[#002855] text-white px-3 py-2 sm:px-5 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold hover:bg-[#4DA8DA] transition-all whitespace-nowrap"
            >
              Registrace
            </Link>
          </div>
        </div>
      </nav>

      {/* --- HERO SEKCE --- */}
      <header className="relative max-w-6xl mx-auto px-6 min-h-screen flex items-center pt-28 pb-16">
        <div className="w-full flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-10">
          {/* Levý sloupec – text */}
          <div className="lg:w-1/2 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#002855] text-xs font-bold uppercase tracking-wide mb-6 border border-blue-100">
              <span className="w-2 h-2 rounded-full bg-[#28A745] animate-pulse"></span>
              Registrace otevřena (Limit: 200)
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 text-[#002855]">
              Dodej každému zápasu <span className="text-gradient">novou šťávu</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Scoreo je tipovačka o čest a slávu. Založ ligu pro svou partu, tipujte výsledky a konečně rozhodněte, kdo je největší sportovní expert. Bez peněz, jen pro ten pocit vítězství.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/register"
                className="text-center bg-[#28A745] text-white text-lg px-8 py-3 rounded-xl font-bold hover:bg-[#218838] transition-all shadow-xl shadow-green-600/20 transform hover:-translate-y-1"
              >
                Vstoupit do hry
              </Link>
              <a
                href="#showcase"
                onClick={(e) => handleNavClick(e, 'showcase')}
                className="text-center bg-white text-[#002855] text-lg px-8 py-3 border border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-colors"
              >
                Jak to funguje?
              </a>
            </div>
          </div>

          {/* Pravý sloupec – mockupy (notebook s telefonem v popředí) */}
          <div className="lg:w-1/2 relative">
            <img
              src="/images/mockup-laptop.png"
              alt="Náhled aplikace Scoreo na notebooku"
              className="w-full h-auto drop-shadow-2xl"
            />
            <img
              src="/images/mockup-mobile.png"
              alt="Náhled aplikace Scoreo na telefonu"
              className="absolute -bottom-6 -right-10 sm:-bottom-8 sm:-right-20 w-3/4 sm:w-3/4 h-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </header>

      {/* --- SHOWCASE: přepínací nabídka --- */}
      <section id="showcase" className="relative scroll-mt-28">
        {/* Animované gradientové pozadí přes celou sekci */}
        <div className="animated-gradient absolute inset-0"></div>

        <div className="relative z-10">
          {/* Přepínač – světle šedý „island" navazující na sekci nad ním */}
          <div className="flex justify-center">
            <div className="relative bg-[#F9FAFB] rounded-b-[40px] px-3 pt-2 pb-3.5 shadow-[0_16px_30px_-12px_rgba(0,0,0,0.25)]">
              {/* Konkávní napojení na sekci nad ním (vykrojené rohy) */}
              <div
                className="absolute top-0 -left-8 w-8 h-8 pointer-events-none"
                style={{ background: 'radial-gradient(circle at bottom left, transparent 32px, #F9FAFB 33px)' }}
              />
              <div
                className="absolute top-0 -right-8 w-8 h-8 pointer-events-none"
                style={{ background: 'radial-gradient(circle at bottom right, transparent 32px, #F9FAFB 33px)' }}
              />
              <div className="inline-flex items-center gap-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 sm:px-6 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-white text-[#002855] shadow-sm'
                        : 'text-gray-500 hover:text-[#002855]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Okno s obsahem */}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-16">
            {renderWindowChrome(
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                  {renderTabPanel()}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </section>

      {/* --- TRUST BAR (Fan Code) --- */}
      <div className="py-12 mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center md:text-left" {...staggerWrap}>

            <motion.div className="flex flex-col items-center md:items-start p-4 md:p-0" {...fadeItem}>
              <span className="text-xl mb-2">🇨🇿</span>
              <span className="font-bold text-lg mb-1 text-[#002855]">Vyrobeno v Čechách</span>
              <span className="text-sm text-gray-500">Hobby projekt - žádný korporát, žádná sázkovka.</span>
            </motion.div>

            <motion.div className="flex flex-col items-center md:items-start p-4 md:p-0" {...fadeItem}>
              <span className="text-xl mb-2">⚡</span>
              <span className="font-bold text-lg mb-1 text-[#002855]">Live data</span>
              <span className="text-sm text-gray-500">Hned víš, jak si stojíš. Žádné čekání na konec zápasu.</span>
            </motion.div>

            <motion.div className="flex flex-col items-center md:items-start p-4 md:p-0" {...fadeItem}>
              <span className="text-xl mb-2">🌍</span>
              <span className="font-bold text-lg mb-1 text-[#002855]">Soutěže</span>
              <span className="text-sm text-gray-500">Fotbal i hokej. Liga, repre, Evropa. Všechno na jednom místě.</span>
            </motion.div>

            <motion.div className="flex flex-col items-center md:items-start p-4 md:p-0" {...fadeItem}>
              <span className="text-xl mb-2">🔒</span>
              <span className="font-bold text-lg mb-1 text-[#002855]">Soukromí</span>
              <span className="text-sm text-gray-500">Data neprodáváme. Analytika je anonymní. Jsi v bezpečí.</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* --- FEATURES GRID (Storytelling) --- */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24 scroll-mt-28">
        <motion.div className="text-center max-w-3xl mx-auto mb-20" {...fadeUp}>
          <h2 className="text-3xl md:text-4xl font-bold text-[#002855] mb-6">
            Proč se přidat do komunity Scoreo?
          </h2>
          <p className="text-gray-600 text-lg">
             Chtěl jsem místo, kde jde o čistou rivalitu. Kde tipy a výsledky létají vzduchem v reálném čase. A kde se můžeš vysmát kolegovi ještě dřív, než rozhodčí pískne konec.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-8" {...staggerWrap}>

          {/* Card 1: Private Leagues */}
          <motion.div className="bg-white p-10 rounded-3xl border border-gray-100 app-card-shadow app-card-hover transition-all duration-300 flex flex-col items-start" {...fadeItemOpacity}>
            <div className="w-14 h-14 bg-[#E3F2FD] rounded-2xl flex items-center justify-center mb-6 text-[#002855]">
               <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#002855]">Válka v kanceláři</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              Založ ligu pro kolegy nebo partu. Hned po výkopu odkrýváme karty – <strong className="text-[#002855]">uvidíš tipy ostatních</strong>, takže "trash talk" může začít už v 5. minutě zápasu.
            </p>
          </motion.div>

          {/* Card 2: Tramvaj (Responsive) */}
          <motion.div className="bg-white p-10 rounded-3xl border border-gray-100 app-card-shadow app-card-hover transition-all duration-300 flex flex-col items-start" {...fadeItemOpacity}>
            <div className="w-14 h-14 bg-[#E3F2FD] rounded-2xl flex items-center justify-center mb-6 text-[#4DA8DA]">
               <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#002855]">Stíháš to i z tramvaje</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              Znáš to. Zápas začíná za 3 minuty a ty jsi teprve na cestě domů. <strong className="text-[#002855]">Web funguje skvěle i na mobilu</strong>. Svůj geniální tip odešleš dřív, než se zavřou dveře. Žádné výmluvy.
            </p>
          </motion.div>

          {/* Card 3: Public Leagues */}
          <motion.div className="bg-white p-10 rounded-3xl border border-gray-100 app-card-shadow app-card-hover transition-all duration-300 flex flex-col items-start" {...fadeItemOpacity}>
            <div className="w-14 h-14 bg-[#E3F2FD] rounded-2xl flex items-center justify-center mb-6 text-[#002855]">
               <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#002855]">Nemáš kamarády?</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              To nevadí! (Teda vadí, ale s tím ti nepomůžu). Zapoj se do veřejných lig a porovnej se s celou republikou. Máme celkové i měsíční žebříčky, takže <strong className="text-[#002855]">můžeš naskočit klidně v půlce sezóny</strong>.
            </p>
          </motion.div>

          {/* Card 4: Trophies */}
          <motion.div className="bg-white p-10 rounded-3xl border border-gray-100 app-card-shadow app-card-hover transition-all duration-300 flex flex-col items-start" {...fadeItemOpacity}>
            <div className="w-14 h-14 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mb-6 text-[#28A745]">
               <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#002855]">Sbírej trofeje, ne tikety</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              Za každý přesný zásah, za každou vítěznou sérii. Plň si profil unikátními odznaky. Protože nic nezahřeje u srdce víc, než digitální odznáček.
            </p>
          </motion.div>

        </motion.div>

        {/* --- AI SECTION (The Helper) --- */}
        <motion.div id="ai" className="mt-12 bg-gradient-to-r from-[#002855] to-[#0f3460] rounded-3xl p-8 md:p-16 text-white shadow-2xl relative overflow-hidden scroll-mt-28" {...fadeUp}>
           {/* Background glow */}
           <div className="absolute top-0 right-0 w-96 h-96 bg-[#7C3AED] opacity-20 blur-[100px] rounded-full translate-x-1/2 -translate-y-1/2"></div>

           <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block bg-[#7C3AED] px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider mb-6 text-white border border-white/20">
                  AI Powered
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-6">Nechat si poradit není ostuda.</h3>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  Nevíš, jak dopadne derby? Můj <span className="text-[#4DA8DA] font-bold">AI Skaut</span> projede historii, formu a data, aby ti dal ten nejlepší tip. Klidně ho použij a pak v hospodě tvrd', že jsi to takhle prostě "cítil".
                </p>
                <div className="flex flex-col sm:flex-row gap-4 text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#28A745] flex items-center justify-center text-[10px]">✓</span>
                    Analýza na základě dat
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#28A745] flex items-center justify-center text-[10px]">✓</span>
                    1 kredit denně (Skaut nebo predikce)
                  </div>
                </div>
              </div>

              {/* AI UI Card */}
              <div className="relative">
                  <div className="absolute -top-6 -right-6 bg-[#28A745] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg transform rotate-12 z-20 hidden md:block">
                    Pssst! Tady je tahák.
                 </div>
                 <div className="bg-white/10 backdrop-blur-md border border-white/10 p-6 rounded-2xl relative z-10 hover:bg-white/15 transition-colors">
                     <div className="flex justify-between items-center mb-6 pb-6 border-b border-white/10">
                        <div>
                            <span className="block text-xs text-gray-400 uppercase tracking-wide">Předpověď</span>
                            <span className="text-lg font-bold">Sparta vs. Slavia</span>
                        </div>
                        <div className="text-right">
                             <span className="block text-xs text-gray-400 uppercase tracking-wide">Důvěra</span>
                             <span className="text-[#7C3AED] font-bold">78%</span>
                        </div>
                     </div>
                     <div className="space-y-4">
                        <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg">
                           <span className="text-gray-300 text-sm">Pravděpodobný vítěz</span>
                           <span className="font-bold">Remíza</span>
                        </div>
                        <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg">
                           <span className="text-gray-300 text-sm">Odhadované skóre</span>
                           <span className="font-bold text-xl text-[#7C3AED]">1 : 1</span>
                        </div>
                     </div>
                 </div>
              </div>
           </div>
        </motion.div>
      </section>

      {/* --- FAQ SEKCE --- */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-24 scroll-mt-28">
        <motion.div className="text-center max-w-2xl mx-auto mb-12" {...fadeUp}>
          <h2 className="text-3xl md:text-4xl font-bold text-[#002855] mb-4">
            Časté dotazy
          </h2>
          <p className="text-gray-600 text-lg">
            Něco ti vrtá hlavou? Tady jsou odpovědi na to nejčastější.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <motion.div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 app-card-shadow overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, ease: 'easeOut', delay: (i % 6) * 0.08 }}
              >
                <button
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-bold text-[#002855] text-lg">{item.q}</span>
                  <svg
                    className={`w-5 h-5 shrink-0 text-[#4DA8DA] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-gray-600 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* --- FOOTER CTA --- */}
      <footer className="py-24 px-6 text-center bg-white border-t border-gray-200">
        <motion.div className="max-w-2xl mx-auto" {...fadeUp}>
          <h2 className="text-4xl font-extrabold text-[#002855] mb-6">
            Připraven na výkop?
          </h2>
          <p className="text-gray-500 mb-10 text-lg">
            Registrace je zatím omezena, aby vše běželo hladce.<br/>
            Utrhni si své místo v sestavě hned teď.
          </p>
          <Link
            to="/register"
            className="inline-block bg-[#002855] text-white text-xl px-12 py-5 rounded-xl font-bold hover:bg-[#4DA8DA] transition-colors shadow-2xl hover:shadow-[#4DA8DA]/30"
          >
            Založit účet zdarma
          </Link>
          <div className="mt-8 text-gray-400">
             Máš nějaký dotaz? <Link to="/contact" className="text-[#002855] font-semibold hover:underline">Napiš nám</Link>
          </div>
          <p className="mt-12 text-sm text-gray-400">
            © {new Date().getFullYear()} Scoreo. Hobby projekt od fanouška pro fanoušky.
          </p>
        </motion.div>
      </footer>
    </div>
  );
};

export default LandingPage;
